import { CreativeLibraryStatus, CreativeStatus, type DxcCreative } from '../../../apis/graphql/types';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_PROPERTY_KEYS,
  BROWSE_ACTIONS,
  COMPONENT_OUTCOMES,
  DCM_PAGES,
  EDIT_TYPES,
  REJECTION_SOURCES,
  UPLOAD_SOURCES,
} from '../constants';
import { trackAnalyticsEvent } from '../core/analyticsClient';
import type { AnalyticsEventProperties } from '../types';
import type { DcmPage } from '../types/eventMap';
import { getCampaignBuilderAnalyticsContext } from '../utils/campaignBuilderAnalyticsContext';
import { isPostLaunchLineItemStatus } from '../utils/isPostLaunchLineItemStatus';
import {
  type CreativeRejectionReasonSource,
  extractCreativeRejectionReasons,
  mapCreativeRejectionReasonsToCodes,
} from '../utils/mapCreativeRejectionReasonsToCodes';
import { trackPostLaunchCreativeSwap } from './postEdit';

type CreativeAnalyticsContext = {
  creativeId: string;
  uploadSource: (typeof UPLOAD_SOURCES)[keyof typeof UPLOAD_SOURCES];
  campaignId?: string;
  adAccountId?: string;
  lineItemId?: string;
  creativeStatus?: string;
  adFormat?: string;
  adDuration?: string;
  page?: DcmPage;
};

const formatCreativeStatus = (status?: string | null): string | undefined => {
  if (!status) {
    return undefined;
  }

  switch (status) {
    case CreativeStatus.UnderReview:
      return 'under-review';
    case CreativeStatus.Submitted:
      return 'submit';
    case CreativeStatus.Rejected:
      return 'reject';
    case CreativeStatus.Invalid:
      return 'invalid';
    case CreativeStatus.Approved:
      return 'approve';
    default:
      return status.toLowerCase().replace(/_/g, '-');
  }
};

const buildCreativeAnalyticsProperties = ({
  creativeId,
  uploadSource,
  campaignId,
  adAccountId,
  lineItemId,
  creativeStatus,
  adFormat,
  adDuration,
  page,
}: CreativeAnalyticsContext): AnalyticsEventProperties => ({
  [ANALYTICS_PROPERTY_KEYS.CREATIVE_ID]: creativeId,
  [ANALYTICS_PROPERTY_KEYS.UPLOAD_SOURCE]: uploadSource,
  [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: campaignId,
  [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: adAccountId,
  [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: lineItemId,
  [ANALYTICS_PROPERTY_KEYS.CREATIVE_STATUS]: creativeStatus,
  [ANALYTICS_PROPERTY_KEYS.AD_FORMAT]: adFormat,
  [ANALYTICS_PROPERTY_KEYS.AD_DURATION]: adDuration,
  [ANALYTICS_PROPERTY_KEYS.PAGE]: page,
  [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
});

export const trackCreativeUploaded = (context: CreativeAnalyticsContext): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.CREATIVE_UPLOADED, buildCreativeAnalyticsProperties(context));
};

export const trackCreativeSubmitted = (context: CreativeAnalyticsContext): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.CREATIVE_SUBMITTED, buildCreativeAnalyticsProperties(context));
};

export type TrackCreativeRejectedParams = CreativeAnalyticsContext & {
  rejectionSource: (typeof REJECTION_SOURCES)[keyof typeof REJECTION_SOURCES];
  rejectionReasonCount?: number;
  rejectionReasons?: string[];
};

export const trackCreativeRejected = ({
  rejectionSource,
  rejectionReasonCount,
  rejectionReasons,
  ...context
}: TrackCreativeRejectedParams): void => {
  const rejectionReasonCodes = rejectionReasons?.length
    ? mapCreativeRejectionReasonsToCodes(rejectionReasons)
    : undefined;

  trackAnalyticsEvent(ANALYTICS_EVENTS.CREATIVE_REJECTED, {
    ...buildCreativeAnalyticsProperties(context),
    [ANALYTICS_PROPERTY_KEYS.REJECTION_SOURCE]: rejectionSource,
    [ANALYTICS_PROPERTY_KEYS.REJECTION_REASON_COUNT]: rejectionReasonCount ?? rejectionReasonCodes?.length,
    ...(rejectionReasonCodes?.length ? { [ANALYTICS_PROPERTY_KEYS.REJECTION_REASONS]: rejectionReasonCodes } : {}),
  });
};

const isCreativeRejectedStatus = (status?: string | null): boolean =>
  status === CreativeStatus.Rejected || status === CreativeStatus.Invalid;

const isCreativeSubmittedStatus = (status?: string | null): boolean =>
  status === CreativeStatus.Submitted || status === CreativeStatus.UnderReview;

const formatDxcCreativeStatus = (status: string): string => {
  switch (status) {
    case CreativeLibraryStatus.PendingApproval:
      return 'under-review';
    case CreativeLibraryStatus.Approved:
      return 'approve';
    case CreativeLibraryStatus.Invalid:
      return 'invalid';
    case CreativeLibraryStatus.Draft:
      return 'draft';
    default:
      return formatCreativeStatus(status);
  }
};

const isDxcCreativeRejectedStatus = (status: string): boolean =>
  status === CreativeLibraryStatus.Invalid || status === CreativeStatus.Rejected || status === CreativeStatus.Invalid;

const isDxcCreativeSubmittedStatus = (status: string): boolean =>
  status === CreativeLibraryStatus.PendingApproval || isCreativeSubmittedStatus(status);

type CreativeUploadAnalyticsParams = Omit<CreativeAnalyticsContext, 'creativeId' | 'creativeStatus'> & {
  creative: { id: string; status?: string | null } & CreativeRejectionReasonSource;
};

export const trackCreativeUploadAnalytics = ({ creative, ...context }: CreativeUploadAnalyticsParams): void => {
  const creativeContext: CreativeAnalyticsContext = {
    ...context,
    creativeId: creative.id,
    creativeStatus: formatCreativeStatus(creative.status),
  };

  trackCreativeUploaded(creativeContext);

  if (isCreativeRejectedStatus(creative.status)) {
    trackCreativeRejected({
      ...creativeContext,
      rejectionSource: REJECTION_SOURCES.UPLOAD_VALIDATION,
      rejectionReasons: extractCreativeRejectionReasons(creative),
    });
    return;
  }

  if (isCreativeSubmittedStatus(creative.status)) {
    trackCreativeSubmitted(creativeContext);
  }

  const builderContext = getCampaignBuilderAnalyticsContext();

  if (
    context.lineItemId &&
    builderContext.lineItemStatus &&
    isPostLaunchLineItemStatus(builderContext.lineItemStatus)
  ) {
    trackPostLaunchCreativeSwap({
      campaignId: context.campaignId ?? builderContext.campaignId,
      lineItemId: context.lineItemId ?? builderContext.lineItemId,
      adAccountId: context.adAccountId ?? builderContext.adAccountId,
      creativeId: creative.id,
      uploadSource: context.uploadSource,
      editType: EDIT_TYPES.CREATIVE,
      politicalClassification: builderContext.politicalClassification,
      industryVertical: builderContext.industryVertical,
      userRole: builderContext.userRole,
      isValidatedEdit: builderContext.isValidatedEdit,
    });
  }
};

type LinkVideoCreativeAnalyticsParams = Omit<CreativeAnalyticsContext, 'creativeId' | 'creativeStatus'> & {
  creative: { id: string; status?: string | null } & CreativeRejectionReasonSource;
  trackSubmitted?: boolean;
  forceSubmitted?: boolean;
};

type AdBuilderCreativeAnalyticsParams = Omit<CreativeAnalyticsContext, 'creativeId' | 'creativeStatus' | 'adFormat'> & {
  creative: DxcCreative;
  trackSubmitted?: boolean;
  forceSubmitted?: boolean;
};

type AssetUploadAnalyticsParams = {
  assetId: string;
  assetSourceId: string | null;
  assetType: string;
  brandId?: string;
  uploadSource: (typeof UPLOAD_SOURCES)[keyof typeof UPLOAD_SOURCES];
  page?: DcmPage;
};

export const trackAssetUploadAnalytics = ({
  assetId,
  assetSourceId,
  assetType,
  brandId,
  uploadSource,
  page,
}: AssetUploadAnalyticsParams): void => {
  if (!assetId) {
    return;
  }

  trackAnalyticsEvent(ANALYTICS_EVENTS.ASSET_UPLOADED, {
    [ANALYTICS_PROPERTY_KEYS.ASSET_ID]: assetId,
    ...(assetSourceId ? { [ANALYTICS_PROPERTY_KEYS.ASSET_SOURCE_ID]: assetSourceId } : {}),
    [ANALYTICS_PROPERTY_KEYS.ASSET_TYPE]: assetType,
    ...(brandId ? { [ANALYTICS_PROPERTY_KEYS.BRAND_ID]: brandId } : {}),
    [ANALYTICS_PROPERTY_KEYS.UPLOAD_SOURCE]: uploadSource,
    [ANALYTICS_PROPERTY_KEYS.PAGE]: page,
    [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
  });
};

export const trackAdBuilderCreativeAnalytics = ({
  creative,
  trackSubmitted = false,
  forceSubmitted = false,
  ...context
}: AdBuilderCreativeAnalyticsParams): void => {
  const creativeContext: CreativeAnalyticsContext = {
    ...context,
    creativeId: creative.creativeId,
    creativeStatus: formatDxcCreativeStatus(creative.status),
    adFormat: creative.adFormat ?? undefined,
  };

  trackCreativeUploaded(creativeContext);

  if (isDxcCreativeRejectedStatus(creative.status)) {
    trackCreativeRejected({
      ...creativeContext,
      rejectionSource: REJECTION_SOURCES.UPLOAD_VALIDATION,
    });
    return;
  }

  if (trackSubmitted && (forceSubmitted || isDxcCreativeSubmittedStatus(creative.status))) {
    trackCreativeSubmitted(creativeContext);
  }
};

export const trackCreativeLinkVideoAnalytics = ({
  creative,
  trackSubmitted = false,
  forceSubmitted = false,
  ...context
}: LinkVideoCreativeAnalyticsParams): void => {
  const creativeContext: CreativeAnalyticsContext = {
    ...context,
    creativeId: creative.id,
    creativeStatus: formatCreativeStatus(creative.status),
  };

  trackCreativeUploaded(creativeContext);

  if (isCreativeRejectedStatus(creative.status)) {
    trackCreativeRejected({
      ...creativeContext,
      rejectionSource: REJECTION_SOURCES.UPLOAD_VALIDATION,
      rejectionReasons: extractCreativeRejectionReasons(creative),
    });
    return;
  }

  if (trackSubmitted && (forceSubmitted || isCreativeSubmittedStatus(creative.status))) {
    trackCreativeSubmitted(creativeContext);
  }
};

type SubmittedLineItem = {
  id: string;
  adFormat?: string | null;
  adDuration?: string | null;
  ads?: Array<{ creative?: { id: string; status?: string | null } | null } | null> | null;
};

export type TrackSubmittedLineItemCreativesParams = {
  lineItems?: SubmittedLineItem[] | null;
  submittedLineItemIds: string[];
  campaignId: string;
  adAccountId?: string;
};

export type TrackCreativeAttachedFromAdGalleryParams = Omit<
  LinkVideoCreativeAnalyticsParams,
  'uploadSource' | 'trackSubmitted'
>;

export const trackCreativeAttachedFromAdGallery = (params: TrackCreativeAttachedFromAdGalleryParams): void => {
  trackCreativeLinkVideoAnalytics({
    ...params,
    uploadSource: UPLOAD_SOURCES.AD_GALLERY,
    trackSubmitted: true,
  });
};

export const trackSubmittedLineItemCreatives = ({
  lineItems,
  submittedLineItemIds,
  campaignId,
  adAccountId,
}: TrackSubmittedLineItemCreativesParams): void => {
  if (!lineItems?.length || !submittedLineItemIds.length) {
    return;
  }

  const submittedLineItemIdSet = new Set(submittedLineItemIds);

  lineItems
    .filter((lineItem) => submittedLineItemIdSet.has(lineItem.id))
    .forEach((lineItem) => {
      const trackedCreativeIds = new Set<string>();

      lineItem.ads?.forEach((ad) => {
        const creative = ad?.creative;

        if (!creative?.id || trackedCreativeIds.has(creative.id)) {
          return;
        }

        trackedCreativeIds.add(creative.id);

        trackCreativeSubmitted({
          creativeId: creative.id,
          uploadSource: UPLOAD_SOURCES.CAMPAIGN_PAYMENT,
          campaignId,
          adAccountId,
          lineItemId: lineItem.id,
          creativeStatus: creative.status ? formatCreativeStatus(creative.status) : undefined,
          adFormat: lineItem.adFormat ?? undefined,
          adDuration: lineItem.adDuration ?? undefined,
          page: DCM_PAGES.CAMPAIGN_PAYMENT,
        });
      });
    });
};

export type TrackContentPackageSelectedParams = {
  packageId: string;
  packageName?: string;
  campaignId?: string;
  lineItemId?: string;
  adAccountId?: string;
};

export const trackContentPackageSelected = ({
  packageId,
  packageName,
  campaignId,
  lineItemId,
  adAccountId,
}: TrackContentPackageSelectedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.CONTENT_PACKAGE_SELECTED, {
    [ANALYTICS_PROPERTY_KEYS.PACKAGE_ID]: packageId,
    ...(packageName ? { [ANALYTICS_PROPERTY_KEYS.PACKAGE_NAME]: packageName } : {}),
    [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: campaignId,
    [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: lineItemId,
    [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: adAccountId,
    [ANALYTICS_PROPERTY_KEYS.PAGE]: DCM_PAGES.CAMPAIGN_BUILDER,
    [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
  });
};

export type TrackContentPackageBrowsedParams = {
  packageId?: string;
  packageName?: string;
  searchQuery?: string;
  campaignId?: string;
  lineItemId?: string;
  adAccountId?: string;
  browseAction: (typeof BROWSE_ACTIONS)[keyof typeof BROWSE_ACTIONS];
};

export const trackContentPackageBrowsed = ({
  packageId,
  packageName,
  searchQuery,
  campaignId,
  lineItemId,
  adAccountId,
  browseAction,
}: TrackContentPackageBrowsedParams): void => {
  trackAnalyticsEvent(ANALYTICS_EVENTS.CONTENT_PACKAGE_BROWSED, {
    [ANALYTICS_PROPERTY_KEYS.PACKAGE_ID]: packageId,
    [ANALYTICS_PROPERTY_KEYS.PACKAGE_NAME]: packageName,
    [ANALYTICS_PROPERTY_KEYS.SEARCH_QUERY]: searchQuery,
    [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: campaignId,
    [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: lineItemId,
    [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: adAccountId,
    [ANALYTICS_PROPERTY_KEYS.BROWSE_ACTION]: browseAction,
    [ANALYTICS_PROPERTY_KEYS.PAGE]: DCM_PAGES.CAMPAIGN_BUILDER,
    [ANALYTICS_PROPERTY_KEYS.COMPONENT_OUTCOME]: COMPONENT_OUTCOMES.USER,
  });
};
